<template>
  <transition name="fade">
    <div
      @click.stop="clicked($event)"
      class="popupWrapper"
      v-if="initialized"
      v-show="show"
      ref="wrapper"
    >
      <article class="popup" role="dialog">
        <div class="popupActions">
          <button
            @click="closePopup"
            class="closeButton"
            type="button"
            :title="$t('popup.close-button')"
          >
            <FaIcon icon="times" />
          </button>
        </div>
        <slot></slot>
      </article>
    </div>
  </transition>
</template>

<script>
import FaIcon from './FaIcon'
import { KeyCodes } from '../../utils/keycodes'

export default {
  name: 'Popup',
  components: {
    FaIcon,
  },
  data() {
    return {
      initialized: false,
      show: false,
    }
  },
  watch: {
    show(show) {
      if (show) {
        this.$emit('open')
      } else {
        this.$emit('close')
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.onBodyClick.bind(this))
    document.addEventListener('keydown', this.onEscape.bind(this), {
      capture: true,
    })
  },

  methods: {
    /**
     * close popup is escape key is clicked
     * */
    onEscape: function(event) {
      // 27 is the escape keycode
      if (this.show && event.key === KeyCodes.ESCAPE) {
        event.stopPropagation()
        event.preventDefault()
        this.closePopup()
      }
    },

    /**
     * This should close the popup whenever the click is outside the popup.
     * */
    onBodyClick: function() {
      if (this.show) {
        this.closePopup()
      }
    },

    /**
     * Preventing defaults so that event doesnt bubble up to body, as we simply want any body click to close the popup
     * @param {Object} event
     */
    clicked: function(event) {
      event.preventDefault()
      event.stopPropagation()
    },

    /**
     * Closes popup
     */
    closePopup() {
      this.show = false
    },

    /**
     * We prevent bubbling and propagation here as well, as the popup would close immediately on show,
     * since we have a body listener that triggers right after.
     * @param {Event=} event
     */
    showPopup(event) {
      if (event) {
        event.preventDefault()
        event.stopPropagation()
      }

      this.initialized = true
      this.show = true
    },

    /**
     * @param {boolean} show
     * @param {Event=} event
     */
    togglePopup(show, event) {
      if (show) {
        this.showPopup(event)
      } else {
        this.closePopup(event)
      }
    },
  },
  destroyed() {
    document.removeEventListener('click', this.onBodyClick.bind(this))
    document.removeEventListener('keydown', this.onEscape.bind(this))
  },
}
</script>

<style lang="scss" scoped>
.popupWrapper {
  position: fixed;
  display: flex;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 20000;
  background: rgba(0, 0, 0, 0.7);

  justify-content: center;
  align-items: center;

  pointer-events: none;

  .popupActions {
    position: absolute;
    top: 1em;
    right: 1em;

    > .closeButton {
      position: relative;
      padding: 0.5em;
      margin-top: -0.5em;
      margin-right: -0.5em;
      font-size: 1.5em;
      border: none;
      background: transparent;
      cursor: pointer;
      outline: 0;
    }
  }
}

.popup {
  font-size: 0.75rem;
  position: relative;
  z-index: 20001;
  box-sizing: border-box;
  min-width: $minWidthPhone;
  max-width: $minWidthDesktop;
  max-height: calc(100vh - 2em);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1.5em 1em 1em;
  pointer-events: auto;

  border-radius: $mainBorderRadius;
  background-color: $frameBackgroundColor;
  box-shadow: $mainBoxShadow;
  -webkit-overflow-scrolling: touch;

  &:focus {
    outline: none;
  }

  h1 {
    font-size: 2em;
  }

  > h2 {
    margin-top: 0.5em;
  }

  ::v-deep .dialog-content-wrapper {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  ::v-deep .dialogButtons {
    margin: 3em -1.2em -1em !important;
  }

  @media screen and (max-width: 40em) {
    max-width: 42em !important;
    min-width: 37em !important;
  }

  // Make the popup fill the entire screen on small devices,
  // so that it feels more like a page view than a popup overlay
  @media screen and (max-width: 32em) {
    position: fixed;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    min-width: 0 !important;
    max-width: 100vw !important;
    max-height: 100vh;
    min-height: 0 !important;
    margin-left: 0 !important;
    margin-top: 0 !important;
    width: auto !important;
    height: auto !important;

    left: 0 !important;
    top: 0 !important;
    right: 0 !important;
    bottom: 0 !important;

    border: 0;
    border-radius: 0;
  }
}

// Transitions CSS
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;

  > .popup {
    transition: transform 0.2s;
  }
}

.fade-enter,
.fade-leave-to {
  opacity: 0;

  > .popup {
    transform: scale(0.75);
  }
}
</style>
